import { ChangeDetectionStrategy, Component, computed, inject, input, model } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { faMoonStars, faSliders, faSunBright } from '@fortawesome/pro-duotone-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';
import { TAppColorMode } from '../../../../../shared/schemas/settings';
import { AppConfigStore } from '../../../../../state/app-config.repository';
import { UserSettingsStore } from '../../../../../state/user-settings.repository';
import { SettingsThemeTabs } from '../theme-tabs/settings-theme-tabs';
import { ThemePresetTile } from '../preset-tile/theme-preset-tile';
import {
  applyThemePreset,
  matchesPreset,
  savedThemePreset,
  THEME_MODES,
  THEME_PRESETS,
  ThemePreset,
} from '../theme-presets';

/**
 * Theme section shared by the setup wizard and App Settings: preset tiles
 * first, the per-mode color/logo/wallpaper editor behind "Advanced". Edits go
 * to the AppConfigStore draft, so the app previews them live and the host
 * decides when to save. The host projects its own heading (`[editor-heading]`).
 */
@Component({
  selector: 'andor-theme-editor',
  standalone: true,
  imports: [TranslatePipe, FaDuotoneIconComponent, SettingsThemeTabs, ThemePresetTile],
  templateUrl: './theme-editor.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './theme-editor.scss',
})
export class ThemeEditor {
  /** Ignores edits while the host is saving. */
  readonly disabled = input(false);
  readonly advanced = model(false);

  protected readonly faSliders = faSliders;
  protected readonly faSunBright = faSunBright;
  protected readonly faMoonStars = faMoonStars;
  protected readonly modes = THEME_MODES;

  private readonly _store = inject(AppConfigStore);
  private readonly _userSettings = inject(UserSettingsStore);

  protected readonly config = toSignal(this._store.appSettings$, {
    initialValue: this._store.value,
  });
  /** The saved custom theme, listed first so the user can go back to it. */
  private readonly _currentTheme = computed(() => savedThemePreset(this._store.saved()));
  readonly tiles = computed<readonly ThemePreset[]>(() => {
    const current = this._currentTheme();
    return current ? [current, ...THEME_PRESETS] : THEME_PRESETS;
  });
  readonly selectedPreset = computed(() =>
    this.tiles().find((preset) => matchesPreset(this.config(), preset))
  );

  applyPreset(preset: ThemePreset) {
    if (this.disabled()) return;
    applyThemePreset(this._store, preset);
  }

  /** Sets the app's default appearance and previews it in the user's own mode. */
  setDefaultMode(mode: TAppColorMode) {
    if (this.disabled()) return;
    this._store.update({ colorMode: mode });
    this._userSettings.update({ colorMode: mode });
  }
}
